import React, { useEffect, useMemo, useState } from "react";
import { ref, get } from "firebase/database";
import { database } from "../../../firebaseConfig";
import StatCard from "./StatCard";
import toast from "react-hot-toast";
import { formatCurrency } from "../../../utils/currencyFormatter";

/**
 * RealTimeBalanceCard - Displays current real-time account balances
 * This shows the cumulative balance across all time periods (not just monthly)
 * Positioned at the top of AccountsOverview to give quick snapshot of current state
 */

const ACCOUNT_TYPES = [
  { key: "farmer_payable", label: "Farmer Payable", color: "#fb7185" },
  { key: "farmer_advance", label: "Farmer Advance", color: "#f59e0b" },
  { key: "liabilities", label: "Liabilities", color: "#8b5cf6" },
  { key: "company_cash", label: "Company Cash", color: "#06b6d4" },
  { key: "company_bank", label: "Company Bank", color: "#0ea5e9" },
  { key: "shop_receivable", label: "Shop Receivable", color: "#84cc16" },
];

const SUMMARY_KEYS = {
  assets: ["company_cash", "company_bank"],
  farmers_owed: ["farmer_payable"],
  customers_owe: ["shop_receivable"],
  liabilities: ["liabilities", "farmer_advance"],
};

const RealTimeBalanceCard = ({ companyKey }) => {
  const [balances, setBalances] = useState({});
  const [loading, setLoading] = useState(true);

  // Load all transactions across all time periods to calculate real-time balances
  useEffect(() => {
    if (!companyKey) {
      setLoading(false);
      return;
    }

    const loadAllBalances = async () => {
      setLoading(true);
      try {
        const calculatedBalances = {};

        // For each account type, fetch all years/months and calculate total
        await Promise.all(
          ACCOUNT_TYPES.map(async (acct) => {
            try {
              const accountPath = `Companies/${companyKey}/Accounts/${acct.key}`;
              const accountRef = ref(database, accountPath);
              const snap = await get(accountRef);

              let totalBalance = 0;

              if (snap.exists()) {
                const accountData = snap.val();

                // Iterate through all years
                Object.keys(accountData || {}).forEach((yearKey) => {
                  const yearData = accountData[yearKey];
                  // Check if yearData is actually a year (should be an object with month keys)
                  if (typeof yearData === 'object' && yearData !== null) {
                    // Iterate through all months in each year
                    Object.keys(yearData || {}).forEach((monthKey) => {
                      const monthData = yearData[monthKey];
                      // Check if monthData is actually a month (should be an object with transaction keys)
                      if (typeof monthData === 'object' && monthData !== null) {
                        // Iterate through all transactions in each month
                        // Using Object.values() here as we only need transaction data, not keys
                        Object.values(monthData || {}).forEach((transaction) => {
                          if (transaction && typeof transaction === 'object') {
                            const amt = Number(transaction.amount || 0);
                            // Credit increases balance, debit decreases it
                            totalBalance += transaction.direction === "credit" ? amt : -amt;
                          }
                        });
                      }
                    });
                  }
                });
              }

              calculatedBalances[acct.key] = totalBalance;
            } catch (err) {
              console.warn(`Error loading ${acct.key}:`, err);
              calculatedBalances[acct.key] = 0;
            }
          })
        );

        setBalances(calculatedBalances);
      } catch (err) {
        console.error("Error loading real-time balances:", err);
        toast.error("Could not load real-time balances");
      } finally {
        setLoading(false);
      }
    };

    loadAllBalances();
  }, [companyKey]);

  // Calculate summary totals
  const summary = useMemo(() => {
    const totals = {};
    totals.assets = SUMMARY_KEYS.assets.reduce((s, k) => s + (balances[k] || 0), 0);
    totals.farmers_owed = SUMMARY_KEYS.farmers_owed.reduce((s, k) => s + (balances[k] || 0), 0);
    totals.customers_owe = SUMMARY_KEYS.customers_owe.reduce((s, k) => s + (balances[k] || 0), 0);
    totals.liabilities = SUMMARY_KEYS.liabilities.reduce((s, k) => s + (balances[k] || 0), 0);
    totals.net = totals.assets - totals.liabilities - totals.farmers_owed + totals.customers_owe;
    return totals;
  }, [balances]);

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-green-50 to-emerald-100 rounded-lg border-2 border-green-300 p-6 shadow-lg mb-6">
        <div className="text-center text-gray-600">Loading real-time balances...</div>
      </div>
    );
  }

  if (!companyKey) {
    return (
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg border-2 border-gray-300 p-6 shadow-lg mb-6">
        <div className="text-center text-gray-600">Company information not available</div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-green-50 to-emerald-100 rounded-lg border-2 border-green-300 p-6 shadow-lg mb-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-xl font-bold text-green-800 flex items-center gap-2">
            <span className="inline-block w-3 h-3 bg-green-500 rounded-full animate-pulse"></span>
            Real-Time Account Balances
          </h3>
          <p className="text-sm text-green-700 mt-1">
            Current account positions across all time periods
          </p>
        </div>
        <div className="text-xs text-green-600 bg-white/60 px-3 py-1 rounded-full">
          Live Data
        </div>
      </div>

      {/* Summary Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <StatCard
          title="Total Assets"
          value={formatCurrency(summary.assets)}
          hint="Cash + Bank accounts"
          accent="from-cyan-400"
        />
        <StatCard
          title="Farmers Owed"
          value={formatCurrency(summary.farmers_owed)}
          hint="Current payable to farmers"
          accent="from-rose-400"
        />
        <StatCard
          title="Customers Owe Us"
          value={formatCurrency(summary.customers_owe)}
          hint="Outstanding receivables"
          accent="from-lime-400"
        />
        <StatCard
          title="Net Position"
          value={formatCurrency(summary.net)}
          hint="Assets - Liabilities"
          accent={summary.net >= 0 ? "from-green-400" : "from-red-400"}
        />
      </div>

      {/* Individual Account Balances */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mt-4">
        {ACCOUNT_TYPES.map((acct) => (
          <div
            key={acct.key}
            className="bg-white/80 rounded-lg p-3 border border-gray-200"
          >
            <div className="flex items-center gap-2 mb-1">
              <span
                style={{ backgroundColor: acct.color }}
                className="inline-block w-2 h-2 rounded-full"
              />
              <div className="text-xs text-gray-600 font-medium">
                {acct.label}
              </div>
            </div>
            <div className="text-lg font-bold text-gray-800">
              {formatCurrency(balances[acct.key] || 0)}
            </div>
          </div>
        ))}
      </div>

      {/* Info Note */}
      <div className="mt-4 text-xs text-green-700 bg-white/60 p-3 rounded">
        Use the monthly view below to track historical trends and changes over time.
      </div>
    </div>
  );
};

export default RealTimeBalanceCard;
